import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper } from '@mui/material';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { logout } from '~/store/users/thunks.ts';
import { MANAGER_LAYOUT_PATHS } from '../constants.ts';
import { guaranteesLayoutStyles } from '../styles.ts';
import type { TGuaranteesNavigationItem } from '../types.ts';
import { getNavigationItemPath, isNavigationItemGroup } from '../utils.ts';
import CompactNavigationSection from './compact/CompactNavigationSection.tsx';
import ExpandedNavigationSection from './expanded/ExpandedNavigationSection.tsx';
import type { INavigationMenuProps } from './types.ts';

const NavigationMenu: React.FC<INavigationMenuProps> = ({
  open,
  currentPath,
  onClose,
  onNavigate,
  onOpen,
  navigation,
}) => {
  const dispatch = useAppDispatch();
  const [expandedGroupSegments, setExpandedGroupSegments] = useState<string[]>([]);
  const [activeCompactGroupSegment, setActiveCompactGroupSegment] = useState<string | null>(null);
  const menuItems = useMemo(
    () => navigation.filter((item): item is TGuaranteesNavigationItem => 'segment' in item),
    [navigation]
  );

  useEffect(() => {
    setActiveCompactGroupSegment(null);
    onClose();
  }, [currentPath, onClose]);

  useEffect(() => {
    const activeGroupSegments = menuItems
      .filter(isNavigationItemGroup)
      .filter((item) => {
        const groupPath = getNavigationItemPath(item);
        return currentPath === groupPath || currentPath.startsWith(`${groupPath}/`);
      })
      .map((item) => item.segment);

    if (activeGroupSegments.length) {
      setExpandedGroupSegments((previousValue) =>
        Array.from(new Set([...previousValue, ...activeGroupSegments]))
      );
    }
  }, [currentPath, menuItems]);

  useEffect(() => {
    if (open) {
      setActiveCompactGroupSegment(null);
    }
  }, [open]);

  const handleNavigate = (path: string) => {
    setActiveCompactGroupSegment(null);
    onClose();
    onNavigate(path);
  };

  const handleToggleGroup = (segment: string) => {
    setExpandedGroupSegments((previousValue) =>
      previousValue.includes(segment)
        ? previousValue.filter((expandedSegment) => expandedSegment !== segment)
        : [...previousValue, segment]
    );
  };

  const handleOpenCompactGroup = (segment: string) => {
    setActiveCompactGroupSegment(null);
    setExpandedGroupSegments((previousValue) =>
      previousValue.includes(segment) ? previousValue : [...previousValue, segment]
    );
    onOpen();
  };

  const handleLogout = async () => {
    setActiveCompactGroupSegment(null);
    onClose();
    await dispatch(logout());
    onNavigate(MANAGER_LAYOUT_PATHS.managerSignIn);
  };

  return (
    <Box sx={guaranteesLayoutStyles.menuDock}>
      <Paper elevation={0} sx={guaranteesLayoutStyles.menuRail(open)}>
        {open ? (
          <ExpandedNavigationSection
            currentPath={currentPath}
            expandedGroupSegments={expandedGroupSegments}
            menuItems={menuItems}
            onLogout={handleLogout}
            onNavigate={handleNavigate}
            onToggleGroup={handleToggleGroup}
          />
        ) : (
          <CompactNavigationSection
            activeCompactGroupSegment={activeCompactGroupSegment}
            currentPath={currentPath}
            menuItems={menuItems}
            onGroupHoverChange={setActiveCompactGroupSegment}
            onGroupOpen={handleOpenCompactGroup}
            onLogout={handleLogout}
            onNavigate={handleNavigate}
          />
        )}
      </Paper>
    </Box>
  );
};

export default NavigationMenu;
